/**
 * Copyright IBM Corp. 2020, 2025
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useRef } from 'react';
import PropTypes from '../../_virtual/index.js';
import cx from 'classnames';
import { OperationalTag, Popover, PopoverContent, Link } from '@carbon/react';
import { pkg } from '../../settings.js';
import { useClickOutside } from '../../global/js/hooks/useClickOutside.js';

const componentName = 'TagSetOverflow';
const blockClass = `${pkg.prefix}--tag-set-overflow`;

// Default values for props
const defaults = {
  allTagsModalSearchThreshold: 10
};
const TagSetOverflow = /*#__PURE__*/React__default.forwardRef((_ref, ref) => {
  let {
    // The component props, in alphabetical order (for consistency).

    allTagsModalSearchThreshold = defaults.allTagsModalSearchThreshold,
    onOverflowClick,
    className,
    onShowAllClick,
    overflowAlign = 'bottom',
    overflowAutoAlign,
    overflowTags,
    overflowType,
    showAllTagsLabel,
    popoverOpen = false,
    setPopoverOpen,
    size,
    // Collect any other property values passed in.
    ...rest
  } = _ref;
  const localRef = useRef(null);
  const overflowTagContent = useRef(null);
  useClickOutside(ref || localRef, () => {
    if (popoverOpen) {
      setPopoverOpen(false);
    }
  });
  const handleShowAllTagsClick = ev => {
    ev.stopPropagation();
    ev.preventDefault();
    setPopoverOpen(false);
    onShowAllClick();
  };
  const handleEscKeyPress = event => {
    const {
      key
    } = event;
    if (key === 'Escape') {
      setPopoverOpen(false);
    }
  };
  const handleOverflowClick = () => {
    // If a custom overflow function is provided then trigger that function
    // on clicking the overflow
    if (onOverflowClick) {
      onOverflowClick(overflowTags);
    } else {
      setPopoverOpen(!popoverOpen);
    }
  };
  if (onOverflowClick) {
    return /*#__PURE__*/React__default.createElement("span", _extends({}, rest, {
      "aria-hidden": overflowTags.length === 0,
      className: cx(`${blockClass}`, {
        [`${blockClass}--hidden`]: overflowTags.length === 0
      }),
      ref: ref || localRef
    }), /*#__PURE__*/React__default.createElement(OperationalTag, {
      onClick: () => handleOverflowClick(),
      className: `${blockClass}__popover-trigger`,
      size: size,
      text: `+${overflowTags.length}`
    }));
  }
  return /*#__PURE__*/React__default.createElement("span", _extends({}, rest, {
    "aria-hidden": overflowTags.length === 0,
    className: cx(`${blockClass}`, {
      [`${blockClass}--hidden`]: overflowTags.length === 0
    }),
    ref: ref || localRef
  }), /*#__PURE__*/React__default.createElement(Popover, {
    align: overflowAlign,
    className: cx(className, `${blockClass}__tagset-popover`),
    dropShadow: true,
    highContrast: true,
    onKeyDown: handleEscKeyPress,
    open: popoverOpen,
    autoAlign: overflowAutoAlign
  }, /*#__PURE__*/React__default.createElement(OperationalTag, {
    onClick: () => setPopoverOpen(!popoverOpen),
    className: cx(`${blockClass}__popover-trigger`),
    size: size,
    text: `+${overflowTags.length}`
  }), /*#__PURE__*/React__default.createElement(PopoverContent, null, /*#__PURE__*/React__default.createElement("div", {
    ref: overflowTagContent,
    className: `${blockClass}__content`
  }, /*#__PURE__*/React__default.createElement("ul", {
    className: `${blockClass}__tag-list`
  }, overflowTags.filter((_, index) => overflowTags.length > allTagsModalSearchThreshold ? index < allTagsModalSearchThreshold : index <= allTagsModalSearchThreshold).map((tag, index) => {
    const tagProps = {};
    if (overflowType === 'tag') {
      tagProps.type = 'high-contrast';
    }
    if (overflowType === 'default') {
      tagProps.filter = false;
    }
    if (/*#__PURE__*/React__default.isValidElement(tag)) {
      return /*#__PURE__*/React__default.createElement("li", {
        className: cx(`${blockClass}__tag-item`, {
          [`${blockClass}__tag-item--default`]: overflowType === 'default',
          [`${blockClass}__tag-item--tag`]: overflowType === 'tag'
        }),
        key: index
      }, /*#__PURE__*/React__default.cloneElement(tag, tagProps));
    }
  })), overflowTags.length > allTagsModalSearchThreshold && /*#__PURE__*/React__default.createElement(Link, {
    className: `${blockClass}__show-all-tags-link`,
    href: "",
    onClick: handleShowAllTagsClick,
    role: "button"
  }, showAllTagsLabel)))));
});
TagSetOverflow.displayName = componentName;
TagSetOverflow.propTypes = {
  /**
   * count of overflowTags over which a modal is offered
   */
  allTagsModalSearchThreshold: PropTypes.number,
  /**
   * className
   */
  className: PropTypes.string,
  /**
   * An optional click handler that overrides the default functionality of displaying all tags in a modal
   */
  onOverflowClick: PropTypes.func,
  /**
   * function to execute on clicking show all
   */
  onShowAllClick: PropTypes.func.isRequired,
  /**
   * overflowAlign from the standard tooltip
   */
  overflowAlign: PropTypes.oneOf(['top', 'top-left', 'top-right', 'bottom', 'bottom-left', 'bottom-right', 'left', 'left-bottom', 'left-top', 'right', 'right-bottom', 'right-top']),
  /**
   * Will auto-align the popover on first render if it is not visible. This prop is currently experimental and is subject to future changes.
   */
  overflowAutoAlign: PropTypes.bool,
  /**
   * tags shown in overflow
   */
  /**@ts-ignore */
  overflowTags: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * Type of rendering displayed inside of the tag overflow component
   */
  overflowType: PropTypes.oneOf(['default', 'tag']),
  /**
   * Open state of the popover
   */
  popoverOpen: PropTypes.bool,
  /**
   * Setter function for the popoverOpen state value
   */
  setPopoverOpen: PropTypes.func.isRequired,
  /**
   * label for the overflow show all tags link
   */
  showAllTagsLabel: PropTypes.string,
  /**
   * Size of the overflow tag
   */
  size: PropTypes.string
};

export { TagSetOverflow };
